<script lang="ts" setup>
    import '../../../../css/choice.css';
    import {onMounted,watch,computed,reactive} from 'vue';   
    import {ClaimDisplay,IClaimTableFormat,ClaimTableWidth,ClaimColumnDefinitions,IClaim,IClaimList} from '../../../../src/lib/Models/ClaimModel';   
    import Claim from '../Subcomponents/Claim.vue';
    import {initPinia} from '../../../lib/PiniaMgr';
    import {usePaymentPlanNewStore} from '../../../lib/Stores/PaymentPlanNewStore'; 
    import {usePaymentPlanDetailsStore} from '../../../lib/Stores/PaymentPlanDetailsStore';
   
    const emit = defineEmits(["selectedItems:model-value"]);
    const paymentPlanNewStore = usePaymentPlanNewStore(initPinia());
    const paymentPlanDetailsStore = usePaymentPlanDetailsStore(initPinia());

    const formData = reactive <
    {   
        claimListing?: IClaimList[],
        displayProcedures: boolean,
        displayPayments: boolean,
        displayComments: boolean,
        disableSelected: boolean,
        currentRow : number;
        listKey: number;
    }>({     
        displayProcedures: false,
        displayPayments: false,
        displayComments: false,
        disableSelected : false,
        currentRow: 0,
        listKey: 0
    });
    const props = withDefaults(defineProps<{ 
        saveEnabled?: number,
        claimList?: IClaim[],
        selection: boolean,
        autodistribute?: boolean,
        display: ClaimDisplay,
        displayProcedures: boolean,
        displayPayments: boolean,
        displayComments: boolean
    }>(),
    {
        saveEnabled: 0, 
        selection: false,
        autodistribute: false,
        display: ClaimDisplay.Full,
        displayProcedures: false,
        displayPayments: false,
        displayComments: false
    }); 
    const claimTableFormat= computed<IClaimTableFormat>(()=> { 
        let newFormat = {inputDisplay: false, inputLabel: "", levelDisplay: false, statusDisplay: false, payerDisplay: false, columns: 6, tableWidth: ClaimTableWidth}; 
        switch (props.display) {
            case ClaimDisplay.PaymentPlan:
            case ClaimDisplay.PaymentPlanIncluded:
            case ClaimDisplay.PaymentPlanDetails:
                newFormat.payerDisplay = true;
                newFormat.statusDisplay = true;  
                break;
            case ClaimDisplay.CreditHeader:
                newFormat.payerDisplay = true;
                newFormat.levelDisplay = true;  
                break;
            case ClaimDisplay.CreditPayment:
            case ClaimDisplay.Refund:
            case ClaimDisplay.RefundTransfer:
                newFormat.levelDisplay = true; 
                newFormat.payerDisplay = true; 
                newFormat.inputDisplay = true; 
                newFormat.inputLabel = "Actions";  
                break;
            case ClaimDisplay.Transfer:
                newFormat.levelDisplay = true; 
                newFormat.payerDisplay = false; 
                newFormat.inputDisplay = true; 
                newFormat.inputLabel = "Allocate";  
                break;
        }
        newFormat.columns += 
            (newFormat.levelDisplay ? 1 : 0) + 
            (newFormat.statusDisplay ? 1 : 0) + 
            (newFormat.payerDisplay ? 1 : 0) + 
            (newFormat.inputDisplay ? 1 : 0);
        newFormat.tableWidth += 
            (newFormat.levelDisplay ? ClaimColumnDefinitions.Level : 0) + 
            (newFormat.statusDisplay ? ClaimColumnDefinitions.Status : 0) + 
            (newFormat.payerDisplay ? ClaimColumnDefinitions.Payer : 0) + 
            (newFormat.inputDisplay ? ClaimColumnDefinitions.Input : 0);
        return newFormat;
    }); 
    onMounted(async () => {  
        if (!props.claimList) {
            return;
        }
        formData.displayProcedures = props.displayProcedures;
        formData.displayPayments = props.displayPayments;
        formData.displayComments = props.displayComments;
        updateClaimListing(props.claimList);
    }); 
    watch(() => props.displayProcedures, (newProcedures : boolean, oldProcedures : boolean) => { 
        if (newProcedures !== oldProcedures) { 
            formData.displayProcedures = newProcedures;
        }
    });
    watch(() => props.displayPayments, (newPayments : boolean, oldPayments : boolean) => { 
        if (newPayments !== oldPayments) { 
            formData.displayPayments = newPayments;   
            if ((newPayments) && (!formData.displayProcedures)) {
                formData.displayProcedures = true;
            }     
        }
    }); 
    watch(() => props.displayComments, (newComments : boolean, oldComments : boolean) => { 
        if (newComments !== oldComments) { 
            formData.displayComments = newComments;    
        }
    });
    
    watch(() => props.claimList, (newclaimList : IClaim[] | undefined, oldclaimList : IClaim[] | undefined) => {  
        if (JSON.stringify(newclaimList) != JSON.stringify(oldclaimList) ) {   
            updateClaimListing(newclaimList);
        } 
    });
    function updateClaimListing(newValues : IClaim[] | undefined) : void {
        formData.listKey++;
        formData.claimListing = newValues?.map(item => ({claimID: item.claimID, selected: false}));
    }
    function isClaimDisabled(claimID : number) : boolean {
        if ((props.display === ClaimDisplay.PaymentPlanIncluded) && (paymentPlanDetailsStore.paidClaims.length > 0)) {
            const search_index = paymentPlanDetailsStore.paidClaims.findIndex(item => (item.claimID == claimID));
            if (search_index != -1) {
                return true;
            }
        }
        return false;
    }
    function setSelectedClaim(index : number) : void {
        if (!formData.claimListing) {
            return;
        }
        formData.currentRow = index;
        formData.claimListing[formData.currentRow].selected = !formData.claimListing[formData.currentRow].selected; 
        if (props.display === ClaimDisplay.PaymentPlan) {
            paymentPlanNewStore.selectClaim(formData.claimListing[formData.currentRow]);
        }        
        else if (props.display === ClaimDisplay.PaymentPlanDetails) {
            paymentPlanDetailsStore.selectPatientClaim(formData.claimListing[formData.currentRow]);
        }        
        else if (props.display === ClaimDisplay.PaymentPlanIncluded) {
            paymentPlanDetailsStore.selectIncludedClaim(formData.claimListing[formData.currentRow]);
        }
        const selectedItems = formData.claimListing.filter(item => item.selected).map(item => item.claimID).flat(); 
        emit("selectedItems:model-value", selectedItems);
    }
</script> 
 
<style scoped> 
.viewDisplay {
    display: block;
}
.viewHide {
    display: none;
}
.cardActions {
    font-size: .85rem;
    font-weight: 500;
    color: #204D89;
}
.billing-header {
  color: #204D89;
  background-color: #f1f1f1;
}
.billing-data-left {
  color: black;
  background-color: white;
  text-align: left;
}    
.billing-data-center {
  color: black;
  background-color: white;
  text-align: center;
}
.billing-data-right {
  color: black;
  background-color: white;
  text-align: right;
}
.billing-cell {
  border: 1px solid #6A8CCB;
  FONT-SIZE: 8pt;
  font-family: Arial, sans-serif, Helvetica;
  padding: 5px 5px 5px 5px;
  cursor: default;
  vertical-align: middle;
}
</style>
<template>
    <div class="row" >
        <div class="col" >
            <table border="0" aria-hidden="true" :style="`table-layout: fixed; border-collapse: collapse; width: ${claimTableFormat.tableWidth}rem;`">
                <colgroup>
                    <col v-if="props.selection" style="width: 1.5rem"/>
                    <col :style="`width: ${ClaimColumnDefinitions.ClaimID}rem;`"/>
                    <col :style="`width: ${ClaimColumnDefinitions.DOS}rem;`"/>
                    <col v-if="claimTableFormat.levelDisplay" :style="`width: ${ClaimColumnDefinitions.Level}rem;`"/>
                    <col v-if="claimTableFormat.statusDisplay" :style="`width: ${ClaimColumnDefinitions.Status}rem;`"/>
                    <col v-if="claimTableFormat.payerDisplay" :style="`width: ${ClaimColumnDefinitions.Payer}rem;`"/>
                    <col :style="`width: ${ClaimColumnDefinitions.Charges}rem;`"/>
                    <col :style="`width: ${ClaimColumnDefinitions.Payments}rem;`"/>
                    <col :style="`width: ${ClaimColumnDefinitions.Adjustments}rem;`"/>
                    <col :style="`width: ${ClaimColumnDefinitions.Balance}rem;`"/>
                    <col v-if="claimTableFormat.inputDisplay" :style="`width: ${ClaimColumnDefinitions.Input}rem;`"/>
                </colgroup>
                <tr>
                    <td  v-if="props.selection" class="billing-header billing-cell text-center">#</td>
                    <td class="billing-header billing-cell text-center">Claim ID</td> 
                    <td class="billing-header billing-cell text-center">Claim DOS</td> 
                    <td v-if="claimTableFormat.levelDisplay" class="billing-header billing-cell text-center">Level</td> 
                    <td v-if="claimTableFormat.statusDisplay" class="billing-header billing-cell text-center">Status</td> 
                    <td v-if="claimTableFormat.payerDisplay" class="billing-header billing-cell text-center">Payer</td>
                    <td class="billing-header billing-cell text-center">Charges</td> 
                    <td class="billing-header billing-cell text-center">Payments</td> 
                    <td class="billing-header billing-cell text-center">Adjustments</td> 
                    <td class="billing-header billing-cell text-center">Balance</td> 
                    <td v-if="claimTableFormat.inputDisplay" class="billing-header billing-cell text-center">{{claimTableFormat.inputLabel}}</td> 
                </tr> 
                <tr class="col" v-for="(claimListRecord,index) in formData.claimListing" :key="`Key${formData.listKey}CLM${claimListRecord.claimID}`">
                    <td  v-if="props.selection" class="billing-data-center billing-cell pt-2" style="vertical-align: top;">
                        <input type="checkbox" :disabled="isClaimDisabled(claimListRecord.claimID)" :checked="claimListRecord.selected" @click="setSelectedClaim(index)">
                    </td>
                    <td :colspan="claimTableFormat.columns" class="billing-cell">
                        <Claim 
                            :saveEnabled="props.saveEnabled"
                            :claimID="claimListRecord.claimID" 
                            :display="props.display"
                            :autodistribute="props.autodistribute"
                            :claimTableFormat="claimTableFormat"
                            :displayProcedures="formData.displayProcedures"
                            :displayPayments="formData.displayPayments"
                            :displayComments="formData.displayComments"
                        />
                    </td>
                </tr>
            </table>    
        </div>
    </div>    
</template>
